import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';
import { useModalClose } from '../../hooks/useModalClose';
import { parseNumberInput, getNumberInputValue } from '../../utils/numberInputHelpers';

export default function PayoutManagement({ theme, user, session, onClose }) {
  const { t } = useLanguage();
  useModalClose(onClose);
  const [view, setView] = useState('list'); // 'list', 'createPayout', or 'createDeposit'
  const [payouts, setPayouts] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    payout_type: 'staff_payment',
    amount: '',
    recipient: '',
    description: ''
  });

  // Deposit form state
  const [depositData, setDepositData] = useState({
    amount: '',
    description: ''
  });

  const isManagerOrOwner = user && (user.user_role === 1 || user.user_role === 4);

  useEffect(() => {
    fetchPayouts();
    fetchDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.session_id]);

  const fetchPayouts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts/session/${session.session_id}`);
      if (response.ok) {
        const data = await response.json();
        setPayouts(data);
      }
    } catch (err) {
      console.error('Error fetching payouts:', err);
      setError(t('error') + ': ' + t('failedToLoadPayouts'));
    }
  };

  const fetchDeposits = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/register-transactions/session/${session.session_id}`);
      if (response.ok) {
        const data = await response.json();
        // Filter for deposits only
        const depositTransactions = data.filter(t => t.transaction_type === 'deposit');
        setDeposits(depositTransactions);
      }
    } catch (err) {
      console.error('Error fetching deposits:', err);
    }
  };

  const handleCreatePayout = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          register_session_id: session.session_id,
          payout_type: formData.payout_type,
          amount: parseFloat(formData.amount),
          recipient: formData.recipient || null,
          description: formData.description || null,
          created_by_user_id: user.id,
          auto_approve: isManagerOrOwner
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payout');
      }

      // Reset form and refresh list
      setFormData({
        payout_type: 'staff_payment',
        amount: '',
        recipient: '',
        description: ''
      });
      setView('list');
      await fetchPayouts();
      await fetchDeposits();
    } catch (err) {
      console.error('Error creating payout:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeposit = async (e) => {
    e.preventDefault();

    if (!isManagerOrOwner) {
      setError(t('insufficientPermissions'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/register-transactions/adjustment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          register_session_id: session.session_id,
          transaction_type: 'deposit',
          amount: parseFloat(depositData.amount),
          description: depositData.description || 'Cash deposit',
          created_by_user_id: user.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create deposit');
      }

      // Reset form and refresh list
      setDepositData({
        amount: '',
        description: ''
      });
      setView('list');
      await fetchPayouts();
      await fetchDeposits();
    } catch (err) {
      console.error('Error creating deposit:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayout = async (payoutId) => {
    if (!isManagerOrOwner) {
      alert(t('insufficientPermissions'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts/${payoutId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve payout');
      }

      await fetchPayouts();
      await fetchDeposits();
    } catch (err) {
      console.error('Error approving payout:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayout = async (payoutId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(t('confirmDelete') + '?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/payouts/${payoutId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete payout');
      }

      await fetchPayouts();
      await fetchDeposits();
    } catch (err) {
      console.error('Error deleting payout:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPayoutTypeLabel = (type) => {
    const typeMap = {
      'staff_payment': t('staffPayment'),
      'credit_tips': t('creditTips'),
      'petty_cash': t('pettyCash'),
      'safe_deposit': t('safeDeposit'),
      'other': t('other')
    };
    return typeMap[type] || type;
  };

  const pendingPayouts = payouts.filter(p => !p.is_approved);
  const approvedPayouts = payouts.filter(p => p.is_approved);
  const totalPending = pendingPayouts.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalApproved = approvedPayouts.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalDeposits = deposits.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

  return (
    <div onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            color: theme?.colors?.text || '#000',
            fontSize: '1.5rem',
            fontWeight: 600
          }}>
            {t('payoutManagement')} - {session.register_name}
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000',
              padding: '0.5rem'
            }}
          >
            ✕
          </button>
        </div>

        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: theme?.colors?.background || '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('totalPayouts')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.text || '#000' }}>
              ${(totalPending + totalApproved).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('approved')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.success || '#4caf50' }}>
              ${totalApproved.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('pending')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.warning || '#ff9800' }}>
              ${totalPending.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('deposits')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.success || '#4caf50' }}>
              ${totalDeposits.toFixed(2)}
            </div>
          </div>
        </div>

        {/* View Tabs */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          <button
            onClick={() => setView('list')}
            style={{
              padding: '0.5rem 0.75rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              border: view === 'list' ? 'none' : `1px solid ${theme?.colors?.border || '#ddd'}`,
              borderRadius: '6px',
              backgroundColor: view === 'list' ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.surface || '#fff'),
              color: view === 'list' ? (theme?.colors?.buttonText || theme?.colors?.surface || '#fff') : (theme?.colors?.text || '#000'),
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flex: '1 1 auto',
              minWidth: '0',
              transition: 'all 0.2s ease'
            }}
          >
            {t('payoutDepositList')}
          </button>
          <button
            onClick={() => setView('createPayout')}
            style={{
              padding: '0.5rem 0.75rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              border: view === 'createPayout' ? 'none' : `1px solid ${theme?.colors?.border || '#ddd'}`,
              borderRadius: '6px',
              backgroundColor: view === 'createPayout' ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.surface || '#fff'),
              color: view === 'createPayout' ? (theme?.colors?.buttonText || theme?.colors?.surface || '#fff') : (theme?.colors?.text || '#000'),
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flex: '1 1 auto',
              minWidth: '0',
              transition: 'all 0.2s ease'
            }}
          >
            {t('createPayout')}
          </button>
          {isManagerOrOwner && (
            <button
              onClick={() => setView('createDeposit')}
              style={{
                padding: '0.5rem 0.75rem',
                fontSize: '0.9rem',
                fontWeight: 500,
                border: view === 'createDeposit' ? 'none' : `1px solid ${theme?.colors?.border || '#ddd'}`,
                borderRadius: '6px',
                backgroundColor: view === 'createDeposit' ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.surface || '#fff'),
                color: view === 'createDeposit' ? (theme?.colors?.buttonText || theme?.colors?.surface || '#fff') : (theme?.colors?.text || '#000'),
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flex: '1 1 auto',
                minWidth: '0',
                transition: 'all 0.2s ease'
              }}
            >
              {t('createDeposit')}
            </button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            backgroundColor: theme?.colors?.surface || '#fff',
            border: `1px solid ${theme?.colors?.error || '#f00'}`,
            borderRadius: '6px',
            color: theme?.colors?.error || '#f00'
          }}>
            {error}
          </div>
        )}

        {/* Create Payout Form */}
        {view === 'createPayout' && (
          <form onSubmit={handleCreatePayout} style={{ marginBottom: '2rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: theme?.colors?.text || '#000'
              }}>
                {t('payoutType')}:
              </label>
              <select
                value={formData.payout_type}
                onChange={(e) => setFormData({ ...formData, payout_type: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: '6px',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000'
                }}
              >
                <option value="staff_payment">{t('staffPayment')}</option>
                <option value="credit_tips">{t('creditTips')}</option>
                <option value="petty_cash">{t('pettyCash')}</option>
                <option value="safe_deposit">{t('safeDeposit')}</option>
                <option value="other">{t('other')}</option>
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: theme?.colors?.text || '#000'
              }}>
                {t('amount')}:
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={getNumberInputValue(formData.amount)}
                onChange={(e) => setFormData({ ...formData, amount: parseNumberInput(e.target.value, { allowDecimals: true, min: 0.01 }) })}
                placeholder="0.00"
                onWheel={(e) => e.target.blur()}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: '6px',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: theme?.colors?.text || '#000'
              }}>
                {t('recipient')} ({t('optional')}):
              </label>
              <input
                type="text"
                value={formData.recipient}
                onChange={(e) => setFormData({ ...formData, recipient: e.target.value })}
                placeholder={t('recipientName')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: '6px',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: theme?.colors?.text || '#000'
              }}>
                {t('description')} ({t('optional')}):
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder={t('payoutDescription')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: '6px',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  resize: 'vertical'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 500,
                border: 'none',
                borderRadius: '6px',
                backgroundColor: theme?.colors?.primary || '#1976d2',
                color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? t('creating') + '...' : t('createPayout')}
            </button>
          </form>
        )}

        {/* Create Deposit Form */}
        {view === 'createDeposit' && isManagerOrOwner && (
          <form onSubmit={handleCreateDeposit} style={{ marginBottom: '2rem' }}>
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: theme?.colors?.background || '#f5f5f5',
              borderRadius: '8px',
              borderLeft: `4px solid ${theme?.colors?.success || '#4caf50'}`
            }}>
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                color: theme?.colors?.text || '#000'
              }}>
                {t('depositInfo')}
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: theme?.colors?.text || '#000'
              }}>
                {t('amount')}:
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={getNumberInputValue(depositData.amount)}
                onChange={(e) => setDepositData({ ...depositData, amount: parseNumberInput(e.target.value, { allowDecimals: true, min: 0.01 }) })}
                onWheel={(e) => e.target.blur()}
                placeholder="0.00"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: '6px',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: theme?.colors?.text || '#000'
              }}>
                {t('description')} ({t('optional')}):
              </label>
              <textarea
                value={depositData.description}
                onChange={(e) => setDepositData({ ...depositData, description: e.target.value })}
                rows={3}
                placeholder={t('depositDescription')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: '6px',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  resize: 'vertical'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 500,
                border: 'none',
                borderRadius: '6px',
                backgroundColor: theme?.colors?.success || '#4caf50',
                color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? t('creating') + '...' : t('createDeposit')}
            </button>
          </form>
        )}

        {/* Payout List */}
        {view === 'list' && (
          <div>
            {/* Pending Payouts */}
            {isManagerOrOwner && pendingPayouts.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  color: theme?.colors?.text || '#000',
                  fontSize: '1.2rem',
                  fontWeight: 600
                }}>
                  {t('pendingApproval')} ({pendingPayouts.length})
                </h3>
                {pendingPayouts.map(payout => (
                  <div
                    key={payout.id}
                    style={{
                      marginBottom: '1rem',
                      padding: '1rem',
                      border: `1px solid ${theme?.colors?.warning || '#ff9800'}`,
                      borderRadius: '8px',
                      backgroundColor: theme?.colors?.surface || '#fff'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          color: theme?.colors?.text || '#000',
                          marginBottom: '0.5rem'
                        }}>
                          {getPayoutTypeLabel(payout.payout_type)} - ${parseFloat(payout.amount).toFixed(2)}
                        </div>
                        {payout.recipient && (
                          <div style={{
                            fontSize: '0.9rem',
                            color: theme?.colors?.textSecondary || '#666',
                            marginBottom: '0.25rem'
                          }}>
                            {t('recipient')}: {payout.recipient}
                          </div>
                        )}
                        {payout.description && (
                          <div style={{
                            fontSize: '0.9rem',
                            color: theme?.colors?.textSecondary || '#666',
                            marginBottom: '0.25rem'
                          }}>
                            {payout.description}
                          </div>
                        )}
                        <div style={{
                          fontSize: '0.85rem',
                          color: theme?.colors?.textSecondary || '#666'
                        }}>
                          {t('created')}: {new Date(payout.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        marginLeft: '1rem'
                      }}>
                        <button
                          onClick={() => handleApprovePayout(payout.id)}
                          disabled={loading}
                          style={{
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: theme?.colors?.success || '#4caf50',
                            color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1
                          }}
                        >
                          {t('approve')}
                        </button>
                        <button
                          onClick={() => handleDeletePayout(payout.id)}
                          disabled={loading}
                          style={{
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            border: `1px solid ${theme?.colors?.error || '#f44336'}`,
                            borderRadius: '6px',
                            backgroundColor: theme?.colors?.surface || '#fff',
                            color: theme?.colors?.error || '#f44336',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1
                          }}
                        >
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Combined Transaction History (Payouts & Deposits) */}
            {(approvedPayouts.length > 0 || deposits.length > 0) && (
              <div>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  color: theme?.colors?.text || '#000',
                  fontSize: '1.2rem',
                  fontWeight: 600
                }}>
                  {t('transactionHistory')} ({approvedPayouts.length + deposits.length})
                </h3>
                {/* Combine and sort transactions by date */}
                {[
                  ...approvedPayouts.map((p, idx) => ({ ...p, type: 'payout', date: new Date(p.approved_at), uniqueId: `payout-${p.id || idx}` })),
                  ...deposits.map((d, idx) => ({ ...d, type: 'deposit', date: new Date(d.created_at), uniqueId: `deposit-${d.id || idx}` }))
                ]
                  .sort((a, b) => b.date - a.date)
                  .map(item => (
                    <div
                      key={item.uniqueId}
                      style={{
                        marginBottom: '1rem',
                        padding: '1rem',
                        border: `1px solid ${
                          item.type === 'deposit'
                            ? (theme?.colors?.success || '#4caf50')
                            : (theme?.colors?.border || '#ddd')
                        }`,
                        borderRadius: '8px',
                        backgroundColor: theme?.colors?.surface || '#fff'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            color: theme?.colors?.text || '#000',
                            marginBottom: '0.5rem'
                          }}>
                            {item.type === 'payout'
                              ? `${getPayoutTypeLabel(item.payout_type)}  -$${parseFloat(item.amount).toFixed(2)}`
                              : `${t('deposit')} +$${parseFloat(item.amount).toFixed(2)}`
                            }
                          </div>
                          {item.type === 'payout' && item.recipient && (
                            <div style={{
                              fontSize: '0.9rem',
                              color: theme?.colors?.textSecondary || '#666',
                              marginBottom: '0.25rem'
                            }}>
                              {t('recipient')}: {item.recipient}
                            </div>
                          )}
                          {item.description && (
                            <div style={{
                              fontSize: '0.9rem',
                              color: theme?.colors?.textSecondary || '#666',
                              marginBottom: '0.25rem'
                            }}>
                              {item.description}
                            </div>
                          )}
                          <div style={{
                            fontSize: '0.85rem',
                            color: theme?.colors?.textSecondary || '#666'
                          }}>
                            {item.type === 'payout'
                              ? `${t('approved')}: ${item.date.toLocaleString()}`
                              : `${t('created')}: ${item.date.toLocaleString()}`
                            }
                          </div>
                        </div>
                        <div style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          backgroundColor: item.type === 'deposit'
                            ? (theme?.colors?.success || '#4caf50')
                            : (theme?.colors?.error || '#f44336'),
                          color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
                          fontSize: '0.85rem',
                          fontWeight: 500
                        }}>
                          {item.type === 'deposit' ? `+${t('deposit')}` : `-${t('payout')}`}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {payouts.length === 0 && deposits.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: theme?.colors?.textSecondary || '#666'
              }}>
                {t('noPayouts')}
              </div>
            )}
          </div>
        )}

        {/* Close Button */}
        <div style={{
          marginTop: '2rem',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 500,
              border: `1px solid ${theme?.colors?.border || '#ddd'}`,
              borderRadius: '6px',
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000',
              cursor: 'pointer'
            }}
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
}
